package com.flowyride.core.domain.navigation

import com.flowyride.core.domain.analysis.bearingTo
import com.flowyride.core.domain.analysis.distanceTo
import com.flowyride.core.domain.analysis.headingChange
import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Maneuver
import com.flowyride.core.domain.model.ManeuverType
import com.flowyride.core.domain.model.Route
import com.flowyride.core.domain.speedcamera.SpeedCamera
import com.flowyride.core.domain.speedcamera.matchedAlongTrack
import kotlin.math.abs
import kotlin.math.max

/** Tunables, to be calibrated on real rides (see the navigation design notes). */
data class NavigationConfig(
    /** Off the line by more than this, heading elsewhere: off route. Plus half the GPS accuracy. */
    val offRouteOffsetMeters: Double = 25.0,
    /** Off the line by more than this, whatever the heading. */
    val offRouteHardOffsetMeters: Double = 50.0,
    /** Heading this far from the line's direction counts as "elsewhere". */
    val offRouteHeadingDegrees: Double = 40.0,
    /** Consecutive fixes needed to declare the rider off route. */
    val offRouteFixes: Int = 2,
    /** Below this speed the GPS heading is noise: only the hard offset applies. */
    val minSpeedForHeadingMetersPerSecond: Double = 8.0 / 3.6,
    /** Back on the line when this close, heading along it. */
    val backOnRouteOffsetMeters: Double = 25.0,
    /** The rejoin point is searched from where the rider left, up to this far along the ride. */
    val rejoinMaxSkipMeters: Double = 5_000.0,
    /** A position this far from the departure needs a way to it first. */
    val toStartThresholdMeters: Double = 100.0,
    val arrivalRadiusMeters: Double = 30.0,
    /** Fixes less precise than this are ignored. */
    val maxAccuracyMeters: Double = 50.0,
    /** Cameras farther than this from the line are not on the ride. */
    val cameraOnRouteMeters: Double = 40.0,
    /** Camera alert distance: speed × this, within [cameraAlertMinMeters, cameraAlertMaxMeters]. */
    val cameraAlertSeconds: Double = 22.0,
    val cameraAlertMinMeters: Double = 300.0,
    val cameraAlertMaxMeters: Double = 800.0,
    /** Two maneuvers this close are shown together ("then…"). */
    val thenManeuverMeters: Double = 300.0,
    /** A new connector is asked for at most this often. */
    val connectorRetryMillis: Long = 10_000,
)

enum class NavigationPhase {
    WAITING_FOR_GPS,
    /** On the way to the departure. */
    TO_START,
    ON_ROUTE,
    /** Left the ride; a way back is being computed (or the network is missing). */
    OFF_ROUTE,
    /** Following the way back onto the ride. */
    REJOINING,
    ARRIVED,
}

data class UpcomingManeuver(val maneuver: Maneuver, val distanceMeters: Double)

data class CameraAhead(val camera: SpeedCamera, val distanceMeters: Double)

/** Why a connector (a route from the rider to the ride) is needed. */
enum class ConnectorKind { TO_START, REJOIN }

/** Everything the navigation screen shows, after each fix. */
data class NavigationSnapshot(
    val phase: NavigationPhase,
    /** Snapped on the followed line when on it; the raw fix otherwise. Null before the first fix. */
    val position: GeoPoint? = null,
    /** Direction to draw the rider's arrow and orient the map. */
    val heading: Double? = null,
    val speedMetersPerSecond: Double? = null,
    val next: UpcomingManeuver? = null,
    /** Right after [next]: shown together. */
    val then: UpcomingManeuver? = null,
    val remainingMeters: Double,
    val remainingSeconds: Double,
    /** How far along the ride (not counting connectors). */
    val progressMeters: Double = 0.0,
    /** Index in the ride's track of the rider's position: the part before is done. */
    val trackIndex: Int = 0,
    /** Way to the departure or back to the ride, when following one. */
    val connector: List<GeoPoint>? = null,
    val cameraAhead: CameraAhead? = null,
    /** Legal limit where the rider is; null when unknown. */
    val speedLimitKmh: Int? = null,
    /** Off route with no connector yet: straight-line direction and distance to the ride. */
    val bearingToRoute: Double? = null,
    val distanceToRouteMeters: Double? = null,
)

sealed interface NavigationEvent {
    /** Compute a [kind] route from [from] to [to], then call [NavigationSession.useConnector]. */
    data class ConnectorNeeded(
        val kind: ConnectorKind,
        val from: GeoPoint,
        val heading: Double?,
        val to: GeoPoint,
        /** Where [to] is along the ride. */
        val targetDistance: Double,
    ) : NavigationEvent

    /** Once per camera, early enough to slow down. */
    data class CameraAlert(val camera: SpeedCamera, val distanceMeters: Double) : NavigationEvent

    /** Index of the stop in the ride's stops (1 = first intermediate stop). */
    data class StopReached(val stopIndex: Int) : NavigationEvent
    data object Arrived : NavigationEvent
}

data class NavigationUpdate(val snapshot: NavigationSnapshot, val events: List<NavigationEvent>)

/**
 * Turn-by-turn guidance along a planned [route], fed with GPS fixes. Pure logic, no clock nor network:
 * it asks for connectors through [NavigationEvent.ConnectorNeeded] and receives them with [useConnector].
 *
 * @param speedCameras cameras to warn about (those away from the ride are ignored).
 */
class NavigationSession(
    private val route: Route,
    speedCameras: List<SpeedCamera> = emptyList(),
    private val config: NavigationConfig = NavigationConfig(),
) {
    private val main = TrackGeometry(route.track.map { it.point })
    private val mainManeuvers = route.maneuvers.along(main)

    /** Stops between departure and arrival, as distances along the ride. */
    private val stopDistances = route.legs.dropLast(1).map { main.distanceAtIndex(it.lastTrackIndex) }
    private var nextStop = 0

    // Each camera is only projected on the segment it stands on: on the whole line, the cameras of the
    // country × a long ride froze the app for a minute when starting.
    private val cameras: List<Pair<SpeedCamera, Double>> = speedCameras
        .matchedAlongTrack(main.points, config.cameraOnRouteMeters)
        .mapNotNull { (camera, segment) ->
            main.project(camera.point, main.distanceAtIndex(segment), main.distanceAtIndex(segment + 1))?.let { camera to it.distanceAlong }
        }
        .sortedBy { it.second }
    private val alertedCameras = mutableSetOf<String>()

    private var progress: Double? = null
    private var phase = NavigationPhase.WAITING_FOR_GPS
    private var offRouteCount = 0
    private var previousFix: LocationFix? = null
    private var lastOffset = 0.0

    private class Connector(
        val kind: ConnectorKind,
        val route: Route,
        val geometry: TrackGeometry,
        val maneuvers: List<Pair<Maneuver, Double>>,
        val durationSeconds: Double,
        val targetDistance: Double,
    ) {
        var progress = 0.0
        var offCount = 0
    }

    private var connector: Connector? = null
    private var connectorRequestedAt: Long? = null
    /** Where the rider left the ride: the rejoin point is searched from there on. */
    private var exitDistance = 0.0

    fun update(fix: LocationFix): NavigationUpdate {
        val events = mutableListOf<NavigationEvent>()
        if (phase == NavigationPhase.ARRIVED) return NavigationUpdate(snapshot(fix, null), events)
        if ((fix.accuracyMeters ?: 0.0) > config.maxAccuracyMeters) return NavigationUpdate(snapshot(fix, null), events)
        val heading = headingOf(fix)
        previousFix = fix

        if (phase == NavigationPhase.WAITING_FOR_GPS) {
            // Already riding the first kilometres: go on from there rather than back to the departure.
            val onFirstKilometres = main.project(fix.point, 0.0, config.rejoinMaxSkipMeters)
                ?.let { it.offsetMeters <= config.backOnRouteOffsetMeters } == true
            if (fix.point.distanceTo(main.points.first()) > config.toStartThresholdMeters && !onFirstKilometres) {
                phase = NavigationPhase.TO_START
                requestConnector(ConnectorKind.TO_START, fix, heading, 0.0, events)
                return NavigationUpdate(snapshot(fix, heading), events)
            }
            phase = NavigationPhase.ON_ROUTE
        }

        connector?.let { current ->
            if (backOnRide(fix, heading, current)) {
                connector = null
                phase = NavigationPhase.ON_ROUTE
            } else {
                followConnector(current, fix, heading, events)
                return NavigationUpdate(snapshot(fix, heading), events)
            }
        }

        if (phase == NavigationPhase.TO_START || phase == NavigationPhase.OFF_ROUTE) {
            val target = if (phase == NavigationPhase.TO_START) 0.0 else exitDistance
            val back = main.project(fix.point, max(0.0, target - WINDOW_BEHIND), target + config.rejoinMaxSkipMeters, headingScore(heading))
            if (back != null && isOn(back, heading, fix)) {
                phase = NavigationPhase.ON_ROUTE
                progress = back.distanceAlong
                offRouteCount = 0
            } else {
                if (connector == null) requestConnector(if (phase == NavigationPhase.TO_START) ConnectorKind.TO_START else ConnectorKind.REJOIN, fix, heading, target, events)
                return NavigationUpdate(snapshot(fix, heading), events)
            }
        }

        followMain(fix, heading, events)
        return NavigationUpdate(snapshot(fix, heading), events)
    }

    /** The route computed after a [NavigationEvent.ConnectorNeeded]. */
    fun useConnector(kind: ConnectorKind, route: Route, targetDistance: Double) {
        if (phase == NavigationPhase.ARRIVED || route.track.size < 2) return
        val geometry = TrackGeometry(route.track.map { it.point })
        connector = Connector(kind, route, geometry, route.maneuvers.along(geometry), route.durationSeconds, targetDistance)
        connectorRequestedAt = null
        phase = if (kind == ConnectorKind.TO_START) NavigationPhase.TO_START else NavigationPhase.REJOINING
    }

    /** The connector could not be computed: keep pointing at the ride, ask again later. */
    fun connectorFailed() {
        // connectorRequestedAt stays set: the retry delay applies.
    }

    private fun followMain(fix: LocationFix, heading: Double?, events: MutableList<NavigationEvent>) {
        val last = progress
        val from = last?.let { it - WINDOW_BEHIND } ?: 0.0
        val to = last?.let { it + max(WINDOW_AHEAD, (fix.speedMetersPerSecond ?: 0.0) * 60) } ?: config.rejoinMaxSkipMeters
        val projection = main.project(fix.point, from, to, headingScore(heading))
            ?.takeIf { it.offsetMeters <= REMATCH_OFFSET_METERS }
            // GPS jump, tunnel, first fix far into the ride: anywhere, but the closest to where we were wins a tie
            // (a loop ends where it starts).
            ?: main.project(fix.point, score = headingScore(heading, near = last ?: 0.0))
            ?: return

        if (isOff(projection, heading, fix)) {
            offRouteCount++
            if (offRouteCount >= config.offRouteFixes) {
                phase = NavigationPhase.OFF_ROUTE
                exitDistance = last ?: projection.distanceAlong
                requestConnector(ConnectorKind.REJOIN, fix, heading, exitDistance, events)
                lastOffset = projection.offsetMeters
                return
            }
        } else {
            offRouteCount = 0
        }
        lastOffset = projection.offsetMeters
        progress = projection.distanceAlong

        while (nextStop < stopDistances.size && projection.distanceAlong >= stopDistances[nextStop] - config.arrivalRadiusMeters) {
            nextStop++
            events += NavigationEvent.StopReached(nextStop)
        }
        cameraAlert(projection.distanceAlong, fix)?.let { events += it }
        if (main.length - projection.distanceAlong <= config.arrivalRadiusMeters) {
            phase = NavigationPhase.ARRIVED
            events += NavigationEvent.Arrived
        }
    }

    private fun followConnector(current: Connector, fix: LocationFix, heading: Double?, events: MutableList<NavigationEvent>) {
        val projection = current.geometry.project(fix.point, current.progress - WINDOW_BEHIND, current.progress + WINDOW_AHEAD, headingScore(heading))
            ?: current.geometry.project(fix.point, score = headingScore(heading))
            ?: return
        if (isOff(projection, heading, fix)) {
            current.offCount++
            if (current.offCount >= config.offRouteFixes) {
                // Missed the way back too: a new one, from here.
                connector = null
                phase = if (current.kind == ConnectorKind.TO_START) NavigationPhase.TO_START else NavigationPhase.OFF_ROUTE
                requestConnector(current.kind, fix, heading, if (current.kind == ConnectorKind.TO_START) 0.0 else exitDistance, events)
                return
            }
        } else {
            current.offCount = 0
        }
        current.progress = projection.distanceAlong
    }

    /** Close to the ride, heading along it, where the connector leads (or anywhere after). */
    private fun backOnRide(fix: LocationFix, heading: Double?, current: Connector): Boolean {
        val from = if (current.kind == ConnectorKind.TO_START) 0.0 else max(0.0, exitDistance - WINDOW_BEHIND)
        val to = max(current.targetDistance, exitDistance) + config.rejoinMaxSkipMeters
        val projection = main.project(fix.point, from, to, headingScore(heading)) ?: return false
        if (!isOn(projection, heading, fix)) return false
        // Near the start of a connector, the ride may pass right by: only leave it once past most of it or on target.
        val nearTarget = abs(projection.distanceAlong - current.targetDistance) <= WINDOW_BEHIND
        val connectorDone = current.geometry.length - current.progress <= WINDOW_BEHIND
        if (!nearTarget && !connectorDone && current.kind == ConnectorKind.TO_START) return false
        progress = projection.distanceAlong
        offRouteCount = 0
        return true
    }

    private fun requestConnector(kind: ConnectorKind, fix: LocationFix, heading: Double?, searchFrom: Double, events: MutableList<NavigationEvent>) {
        val requestedAt = connectorRequestedAt
        if (requestedAt != null && fix.timeMillis - requestedAt < config.connectorRetryMillis) return
        connectorRequestedAt = fix.timeMillis
        val target = when (kind) {
            ConnectorKind.TO_START -> 0.0
            ConnectorKind.REJOIN -> rejoinDistance(fix.point, searchFrom)
        }
        events += NavigationEvent.ConnectorNeeded(kind, fix.point, heading, main.pointAt(target), target)
    }

    /** The point of the ride closest to [point], between [from] and [NavigationConfig.rejoinMaxSkipMeters] further. */
    private fun rejoinDistance(point: GeoPoint, from: Double): Double {
        val to = (from + config.rejoinMaxSkipMeters).coerceAtMost(main.length)
        var best = from
        var bestDistance = Double.MAX_VALUE
        var d = from
        while (d <= to) {
            val distance = point.distanceTo(main.pointAt(d))
            if (distance < bestDistance) {
                bestDistance = distance
                best = d
            }
            d += REJOIN_STEP_METERS
        }
        // Slightly ahead: the connector should join the ride, not end right at its edge going the wrong way.
        return (best + REJOIN_AHEAD_METERS).coerceAtMost(main.length)
    }

    private fun isOff(projection: TrackGeometry.Projection, heading: Double?, fix: LocationFix): Boolean {
        val tolerance = (fix.accuracyMeters ?: 0.0) / 2
        if (projection.offsetMeters > config.offRouteHardOffsetMeters + tolerance) return true
        if (heading == null || !isMoving(fix)) return false
        val wrongWay = abs(headingChange(projection.bearing, heading)) > config.offRouteHeadingDegrees
        val drifting = projection.offsetMeters > config.offRouteOffsetMeters + tolerance && projection.offsetMeters >= lastOffset
        return wrongWay && drifting
    }

    private fun isOn(projection: TrackGeometry.Projection, heading: Double?, fix: LocationFix): Boolean {
        if (projection.offsetMeters > config.backOnRouteOffsetMeters + (fix.accuracyMeters ?: 0.0) / 2) return false
        if (heading == null || !isMoving(fix)) return true
        return abs(headingChange(projection.bearing, heading)) <= config.offRouteHeadingDegrees
    }

    private fun isMoving(fix: LocationFix) = (fix.speedMetersPerSecond ?: 0.0) >= config.minSpeedForHeadingMetersPerSecond

    /** Among nearby candidates, prefer the one going the rider's way (e.g. the other side of a hairpin). */
    private fun headingScore(heading: Double?, near: Double? = null): (TrackGeometry.Projection) -> Double = { projection ->
        val turn = heading?.let { abs(headingChange(projection.bearing, it)) / 180.0 * HEADING_PENALTY_METERS } ?: 0.0
        val jump = near?.let { abs(projection.distanceAlong - it) / 1_000.0 * JUMP_PENALTY_METERS_PER_KM } ?: 0.0
        projection.offsetMeters + turn + jump
    }

    /** GPS course when moving; else the direction from the previous fix, if it moved enough. */
    private fun headingOf(fix: LocationFix): Double? {
        fix.bearing?.let { if (isMoving(fix)) return it }
        val previous = previousFix ?: return null
        return if (previous.point.distanceTo(fix.point) >= MIN_MOVE_FOR_HEADING_METERS) previous.point.bearingTo(fix.point) else null
    }

    private fun cameraAlert(at: Double, fix: LocationFix): NavigationEvent.CameraAlert? {
        val threshold = ((fix.speedMetersPerSecond ?: 0.0) * config.cameraAlertSeconds).coerceIn(config.cameraAlertMinMeters, config.cameraAlertMaxMeters)
        val (camera, distance) = cameras.firstOrNull { (camera, d) -> d > at && d - at <= threshold && camera.id !in alertedCameras } ?: return null
        alertedCameras += camera.id
        return NavigationEvent.CameraAlert(camera, distance - at)
    }

    private fun snapshot(fix: LocationFix, heading: Double?): NavigationSnapshot {
        val current = connector
        val mainProgress = progress ?: 0.0
        val mainRemaining = when (phase) {
            NavigationPhase.ARRIVED -> 0.0
            NavigationPhase.TO_START, NavigationPhase.WAITING_FOR_GPS -> main.length
            else -> main.length - (current?.targetDistance ?: mainProgress)
        }.coerceAtLeast(0.0)
        val mainSeconds = if (main.length > 0) route.durationSeconds * mainRemaining / main.length else 0.0

        return when {
            phase == NavigationPhase.ARRIVED -> NavigationSnapshot(
                phase = phase,
                position = main.points.last(),
                heading = heading,
                speedMetersPerSecond = fix.speedMetersPerSecond,
                remainingMeters = 0.0,
                remainingSeconds = 0.0,
                progressMeters = main.length,
                trackIndex = main.points.lastIndex,
            )
            current != null -> {
                val left = (current.geometry.length - current.progress).coerceAtLeast(0.0)
                val connectorSeconds = if (current.geometry.length > 0) current.durationSeconds * left / current.geometry.length else 0.0
                val (next, then) = upcoming(current.maneuvers, current.progress)
                NavigationSnapshot(
                    phase = phase,
                    position = current.geometry.pointAt(current.progress),
                    heading = heading ?: current.geometry.bearingAt(current.progress),
                    speedMetersPerSecond = fix.speedMetersPerSecond,
                    next = next,
                    then = then,
                    remainingMeters = left + mainRemaining,
                    remainingSeconds = connectorSeconds + mainSeconds,
                    progressMeters = mainProgress,
                    trackIndex = main.segmentAt(if (current.kind == ConnectorKind.TO_START) 0.0 else exitDistance),
                    connector = current.geometry.points,
                    speedLimitKmh = current.route.speedLimitAt(current.geometry.segmentAt(current.progress)),
                )
            }
            phase == NavigationPhase.ON_ROUTE -> {
                val (next, then) = upcoming(mainManeuvers, mainProgress)
                val cameraAhead = cameras.firstOrNull { it.second > mainProgress }
                    ?.takeIf { it.first.id in alertedCameras }
                    ?.let { CameraAhead(it.first, it.second - mainProgress) }
                NavigationSnapshot(
                    phase = phase,
                    position = main.pointAt(mainProgress),
                    heading = heading ?: main.bearingAt(mainProgress),
                    speedMetersPerSecond = fix.speedMetersPerSecond,
                    next = next,
                    then = then,
                    remainingMeters = mainRemaining,
                    remainingSeconds = mainSeconds,
                    progressMeters = mainProgress,
                    trackIndex = main.segmentAt(mainProgress),
                    cameraAhead = cameraAhead,
                    speedLimitKmh = route.speedLimitAt(main.segmentAt(mainProgress)),
                )
            }
            else -> {
                // Waiting for a connector: point at the ride.
                val target = if (phase == NavigationPhase.TO_START) 0.0 else rejoinDistance(fix.point, exitDistance)
                val targetPoint = main.pointAt(target)
                NavigationSnapshot(
                    phase = phase,
                    position = fix.point,
                    heading = heading,
                    speedMetersPerSecond = fix.speedMetersPerSecond,
                    remainingMeters = mainRemaining + fix.point.distanceTo(targetPoint),
                    remainingSeconds = mainSeconds,
                    progressMeters = mainProgress,
                    trackIndex = main.segmentAt(if (phase == NavigationPhase.TO_START) 0.0 else exitDistance),
                    bearingToRoute = fix.point.bearingTo(targetPoint),
                    distanceToRouteMeters = fix.point.distanceTo(targetPoint),
                )
            }
        }
    }

    private fun upcoming(maneuvers: List<Pair<Maneuver, Double>>, at: Double): Pair<UpcomingManeuver?, UpcomingManeuver?> {
        val index = maneuvers.indexOfFirst { (maneuver, d) -> maneuver.type.isAction && d > at + PASSED_MANEUVER_METERS }
        if (index < 0) return null to null
        val (maneuver, distance) = maneuvers[index]
        val next = UpcomingManeuver(maneuver, distance - at)
        val then = maneuvers.drop(index + 1).firstOrNull { it.first.type.isAction }
            ?.takeIf { it.second - distance <= config.thenManeuverMeters }
            ?.let { UpcomingManeuver(it.first, it.second - at) }
        return next to then
    }

    private fun Route.speedLimitAt(trackIndex: Int): Int? =
        speedLimits.firstOrNull { trackIndex >= it.startTrackIndex && trackIndex < it.endTrackIndex }?.speedLimitKmh

    private fun List<Maneuver>.along(geometry: TrackGeometry): List<Pair<Maneuver, Double>> =
        filter { it.type != ManeuverType.DEPART }.map { it to geometry.distanceAtIndex(it.trackIndex) }.sortedBy { it.second }

    private companion object {
        /** Matching window around the last known position. */
        const val WINDOW_BEHIND = 200.0
        const val WINDOW_AHEAD = 2_000.0

        /** Farther than this from the window's best match: look along the whole line (GPS jump, tunnel). */
        const val REMATCH_OFFSET_METERS = 150.0

        /** A full U-turn costs as much as being this far from the line. */
        const val HEADING_PENALTY_METERS = 60.0
        const val MIN_MOVE_FOR_HEADING_METERS = 8.0

        /** When matching along the whole line: distance along it matters a little. */
        const val JUMP_PENALTY_METERS_PER_KM = 2.0
        const val REJOIN_STEP_METERS = 25.0
        const val REJOIN_AHEAD_METERS = 50.0

        /** Right behind the rider, a maneuver is done. */
        const val PASSED_MANEUVER_METERS = 5.0
    }
}
